// TourPackages.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// Import Images
import tourPackagesBg from '../assets/pick/40.jpg';
import package1 from '../assets/pick/38.jpg';
import package2 from '../assets/pick/33.jpg';
import package3 from '../assets/pick2/Anuradapura/istockphoto-1178654295-612x612.jpg';
import package4 from '../assets/pick2/yala/istockphoto-579436668-612x612.jpg';

// === FRAMER MOTION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.6 } },
};

const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } },
    exit: { opacity: 0, scale: 0.8, transition: { duration: 0.3 } },
};

// === PACKAGES DATA (Tradotto con t()) ===
const TourPackages = () => {
    const { t } = useTranslation();
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const packages = [
        {
            id: 1,
            image: package1,
            title: t('tourPackages.classic7.title'),
            duration: t('tourPackages.classic7.duration'),
            description: t('tourPackages.classic7.description'),
            category: t('tourPackages.classic7.category'),
            itinerary: t('tourPackages.classic7.itinerary', { returnObjects: true }),
            includes: t('tourPackages.classic7.includes', { returnObjects: true }),
            excludes: t('tourPackages.classic7.excludes', { returnObjects: true }),
        },
        {
            id: 2,
            image: package2,
            title: t('tourPackages.northern11.title'),
            duration: t('tourPackages.northern11.duration'),
            description: t('tourPackages.northern11.description'),
            category: t('tourPackages.northern11.category'),
            itinerary: t('tourPackages.northern11.itinerary', { returnObjects: true }),
            includes: t('tourPackages.northern11.includes', { returnObjects: true }),
            excludes: t('tourPackages.northern11.excludes', { returnObjects: true }),
        },
        {
            id: 3,
            image: package3,
            title: t('tourPackages.meditation9.title'),
            duration: t('tourPackages.meditation9.duration'),
            description: t('tourPackages.meditation9.description'),
            category: t('tourPackages.meditation9.category'),
            itinerary: t('tourPackages.meditation9.itinerary', { returnObjects: true }),
            includes: t('tourPackages.meditation9.includes', { returnObjects: true }),
            excludes: t('tourPackages.meditation9.excludes', { returnObjects: true }),
        },
        {
            id: 4,
            image: package4,
            title: t('tourPackages.classic9.title'),
            duration: t('tourPackages.classic9.duration'),
            description: t('tourPackages.classic9.description'),
            category: t('tourPackages.classic9.category'),
            itinerary: t('tourPackages.classic9.itinerary', { returnObjects: true }),
            includes: t('tourPackages.classic9.includes', { returnObjects: true }),
            excludes: t('tourPackages.classic9.excludes', { returnObjects: true }),
        },
    ];

    const openModal = (pkg) => {
        setSelectedPackage(pkg);
        setIsModalOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedPackage(null);
        document.body.style.overflow = 'unset';
    };

    const sendWhatsAppMessage = (pkg) => {
        const phone = "94770806767"; // Replace with your WhatsApp number
        const message = t('tourPackages.whatsappMessage', {
            title: pkg.title,
            duration: pkg.duration,
        });
        const encodedMessage = encodeURIComponent(message);
        const whatsappURL = `https://wa.me/${phone}?text=${encodedMessage}`;
        window.open(whatsappURL, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="bg-black min-h-screen">
            {/* HERO SECTION */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative flex h-[60vh] w-full items-center justify-center font-redHatDisplay md:h-[50vh] lg:h-[80vh]"
            >
                <img
                    src={tourPackagesBg}
                    alt={t('tourPackages.heroAlt')}
                    className="absolute inset-0 h-full w-full object-cover"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#020202c7] to-[#42424259]" />

                <div className="container relative z-10 flex items-center justify-center pt-[100px]">
                    <div className="w-full text-center">
                        <h1 className="text-[50px] font-bold leading-[80px] text-white font-konkhmer md:text-[60px] lg:text-[80px]">
                            {t('tourPackages.pageTitle')}
                        </h1>
                        <p className="mt-6 max-w-[90%] font-redHatDisplay text-[16px] font-semibold tracking-wider text-[#96BA33] sm:mt-10 sm:max-w-2xl sm:text-[25px] mx-auto">
                            {t('tourPackages.subtitle')}
                        </p>

                        <div className="absolute left-[30px] top-10 hidden md:block">
                            <svg width="289" height="245" viewBox="0 0 289 245" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M289 1H1V244.5" stroke="white" strokeOpacity="0.3" />
                            </svg>
                        </div>
                        <div className="absolute -bottom-10 right-[30px] hidden md:block">
                            <svg width="289" height="244" viewBox="0 0 289 244" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 243.5H288V0" stroke="white" strokeOpacity="0.3" />
                            </svg>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* PACKAGES SECTION */}
            <section className="container mx-auto px-4 py-16 lg:py-20">
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    variants={sectionVariants}
                    className="flex flex-col items-center text-center"
                >
                    <h2 className="text-4xl md:text-5xl font-bold text-white font-konkhmer">
                        <span
                            style={{
                                background: 'linear-gradient(to right, #009CE6, #96BA33)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                            }}
                        >
                            {t('tourPackages.sectionTitle')}
                        </span>
                    </h2>
                    <p className="mt-4 text-lg text-gray-300 max-w-3xl">
                        {t('tourPackages.sectionDesc')}
                    </p>
                </motion.div>

                {/* 4 CARDS GRID */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={{
                        hidden: { opacity: 0 },
                        visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
                    }}
                    className="mt-12 grid gap-8 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
                >
                    {packages.map((pkg) => (
                        <motion.div
                            key={pkg.id}
                            variants={cardVariants}
                            className="group relative overflow-hidden rounded-2xl bg-gray-900 border border-gray-800 transition-all duration-500 hover:border-[#009CE6] hover:shadow-2xl hover:shadow-[#009CE6]/30"
                        >
                            <div className="relative h-64 overflow-hidden">
                                <img
                                    src={pkg.image}
                                    alt={pkg.title}
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            </div>

                            <div className="p-6 space-y-3">
                                <h3 className="text-xl font-bold text-white font-redHatDisplay line-clamp-2">
                                    {pkg.title}
                                </h3>
                                <p className="text-sm text-[#96BA33] font-semibold">
                                    {pkg.duration} • {pkg.category}
                                </p>
                                <p className="text-sm text-gray-400 line-clamp-3">
                                    {pkg.description}
                                </p>

                                <button
                                    onClick={() => openModal(pkg)}
                                    className="mt-4 w-full rounded-full bg-gradient-to-r from-[#009CE6] to-[#96BA33] py-3 text-white font-semibold text-sm transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:shadow-[#009CE6]/50"
                                >
                                    {t('tourPackages.viewDetails')}
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </section>

            {/* MODAL */}
            {isModalOpen && selectedPackage && (
                <motion.div
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={modalVariants}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
                    onClick={closeModal}
                >
                    <motion.div
                        className="relative max-w-4xl w-full max-h-[90vh] overflow-y-auto bg-gray-900 rounded-2xl shadow-2xl border border-[#009CE6]/50 p-6 md:p-8"
                        onClick={(e) => e.stopPropagation()}
                        variants={modalVariants}
                    >
                        <button
                            onClick={closeModal}
                            className="absolute top-4 right-4 text-white hover:text-[#96BA33] transition "
                        >
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="text-center mb-8 mt-10">
                            <h2 className="text-3xl md:text-4xl font-bold text-white font-konkhmer">
                                {selectedPackage.title}
                            </h2>
                            <p className="mt-2 text-[#96BA33] font-semibold">
                                {selectedPackage.duration} • {selectedPackage.category}
                            </p>
                        </div>

                        <div className="space-y-6">
                            {selectedPackage.itinerary.map((day, idx) => (
                                <div key={idx} className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
                                    <h3 className="text-lg font-bold text-[#009CE6] mb-1">{day.day}</h3>
                                    {day.hotel && <p className="text-sm text-gray-300 mb-1"><strong>{t('tourPackages.hotel')}:</strong> {day.hotel}</p>}
                                    {day.meals && <p className="text-sm text-gray-300 mb-2"><strong>{t('tourPackages.meals')}:</strong> {day.meals}</p>}
                                    <div
                                        className="text-sm text-gray-200 leading-relaxed"
                                        dangerouslySetInnerHTML={{ __html: day.details }}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="mt-8 grid md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="text-lg font-bold text-[#96BA33] mb-3">{t('tourPackages.included')}</h4>
                                <ul className="space-y-1 text-sm text-gray-300">
                                    {selectedPackage.includes.map((item, i) => (
                                        <li key={i} className="flex items-start">
                                            <span className="text-[#96BA33] mr-2">✓</span> {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <h4 className="text-lg font-bold text-red-400 mb-3">{t('tourPackages.excluded')}</h4>
                                <ul className="space-y-1 text-sm text-gray-300">
                                    {selectedPackage.excludes.map((item, i) => (
                                        <li key={i} className="flex items-start">
                                            <span className="text-red-400 mr-2">✗</span> {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <div className="mt-10 text-center">
                            <button
                                onClick={() => sendWhatsAppMessage(selectedPackage)}
                                className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-[#25D366] to-[#128C7E] px-8 py-4 text-white font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#25D366]/30"
                            >
                                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.208-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.263c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.884 3.488" />
                                </svg>
                                {t('tourPackages.bookOnWhatsApp')}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </div>
    );
};

export default TourPackages;